import type { CSSProperties } from "react";
import { tokens } from "../theme";

const SHIMMER_HEADER = "avatar-panel-shimmer-header";
const SHIMMER_BODY = "avatar-panel-shimmer-body";

const NEUTRAL = {
  canvas: "#f9fafb",
  header: "#e5e7eb",
  border: "#e5e7eb",
} as const;

const styles: Record<string, CSSProperties> = {
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: "100vh",
    background: NEUTRAL.canvas,
    overflow: "hidden",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.space[3],
    padding: `${tokens.space[4]} ${tokens.space[4]} ${tokens.space[5]}`,
    background: NEUTRAL.header,
    flexShrink: 0,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: tokens.radius.pill,
    flexShrink: 0,
    opacity: 0.35,
  },
  headerText: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.space[2],
  },
  titleLine: {
    height: 16,
    width: "62%",
    borderRadius: tokens.radius.sm,
    opacity: 0.35,
  },
  subtitleLine: {
    height: 12,
    width: "48%",
    borderRadius: tokens.radius.sm,
    opacity: 0.28,
  },
  body: {
    flex: 1,
    padding: tokens.space[4],
    display: "flex",
    flexDirection: "column",
    gap: tokens.space[4],
  },
  greetingBlock: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.space[2],
    marginBottom: tokens.space[2],
  },
  line: {
    height: 12,
    borderRadius: tokens.radius.sm,
  },
  chipRow: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.space[2],
  },
  chip: {
    height: 44,
    borderRadius: tokens.radius.lg,
  },
  footer: {
    padding: `${tokens.space[3]} ${tokens.space[4]} ${tokens.space[4]}`,
    borderTop: `1px solid ${NEUTRAL.border}`,
    background: NEUTRAL.canvas,
    flexShrink: 0,
  },
  input: {
    height: 48,
    borderRadius: tokens.radius.lg,
  },
  poweredBy: {
    height: 10,
    width: 96,
    borderRadius: tokens.radius.sm,
    margin: `${tokens.space[2]} auto 0`,
    opacity: 0.5,
  },
} as const satisfies Record<string, CSSProperties>;

function ShimmerBlock({
  style,
  variant = "body",
}: {
  style: CSSProperties | undefined;
  variant?: "header" | "body";
}) {
  return (
    <div
      className={variant === "header" ? SHIMMER_HEADER : SHIMMER_BODY}
      style={style}
      aria-hidden="true"
    />
  );
}

/** Skeleton shimmer shown while config + session bootstrap is in flight. */
export default function PanelLoadingShimmer() {
  return (
    <>
      <style>{`
        @keyframes avatar-panel-shimmer-slide {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .${SHIMMER_HEADER} {
          background: linear-gradient(
            90deg,
            #e5e7eb 0%,
            #d1d5db 50%,
            #e5e7eb 100%
          );
          background-size: 200% 100%;
          animation: avatar-panel-shimmer-slide 1.5s ease-in-out infinite;
        }
        .${SHIMMER_BODY} {
          background: linear-gradient(
            90deg,
            #f3f4f6 0%,
            #e5e7eb 50%,
            #f3f4f6 100%
          );
          background-size: 200% 100%;
          animation: avatar-panel-shimmer-slide 1.5s ease-in-out infinite;
        }
      `}</style>
      <div
        style={styles.root}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label="Loading chat"
      >
        <div style={styles.header}>
          <ShimmerBlock variant="header" style={styles.avatar} />
          <div style={styles.headerText}>
            <ShimmerBlock variant="header" style={styles.titleLine} />
            <ShimmerBlock variant="header" style={styles.subtitleLine} />
          </div>
        </div>

        <div style={styles.body}>
          <div style={styles.greetingBlock}>
            <ShimmerBlock style={{ ...styles.line, width: "88%" }} />
            <ShimmerBlock style={{ ...styles.line, width: "72%" }} />
          </div>
          <div style={styles.chipRow}>
            <ShimmerBlock style={styles.chip} />
            <ShimmerBlock style={styles.chip} />
            <ShimmerBlock style={{ ...styles.chip, width: "85%" }} />
          </div>
        </div>

        <div style={styles.footer}>
          <ShimmerBlock style={styles.input} />
          <ShimmerBlock style={styles.poweredBy} />
        </div>
      </div>
    </>
  );
}
